import React, { useId, useMemo, useState } from "react";

export default function AppRadioGroup({
  options = [],
  value,
  defaultValue = "",
  onChange,
  name,
  direction = "column",
  className = "",
  labelVariant = "label-1",
  invalid = false,
  disabled = false,
  ...props
}) {
  const generatedName = useId();
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue);
  const currentValue = isControlled ? value : internalValue;
  const normalizedOptions = useMemo(
    () =>
      options.map((option) =>
        typeof option === "string" ? { value: option, label: option } : option
      ),
    [options]
  );

  const classes = [
    "ds-radio-group",
    direction === "row" ? "ds-radio-group--row" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  function handleChange(event, option) {
    if (!isControlled) {
      setInternalValue(option.value);
    }
    if (onChange) {
      event.target.option = option;
      if (event.currentTarget) {
        event.currentTarget.option = option;
      }
      onChange(event);
    }
  }

  return (
    <div className={classes} role="radiogroup" aria-invalid={invalid ? "true" : undefined} {...props}>
      {normalizedOptions.map((option) => {
        const optionDisabled = disabled || Boolean(option.disabled);
        const checked = option.value === currentValue;
        const hasSupportingText = Boolean(option.description);
        return (
          <label
            key={option.value}
            className={`ds-radio-field${checked ? " ds-radio-field--checked" : ""}${
              optionDisabled ? " ds-radio-field--disabled" : ""
            }${invalid ? " ds-radio-field--invalid" : ""}${
              hasSupportingText ? "" : " ds-radio-field--single-line"
            }`}
          >
            <input
              className="ds-radio__input"
              type="radio"
              name={name || generatedName}
              value={option.value}
              checked={checked}
              disabled={optionDisabled}
              onChange={(event) => handleChange(event, option)}
            />
            <span className="ds-radio__control" aria-hidden="true">
              <span className="ds-radio__box">
                <span className="ds-radio__dot">
                  <svg viewBox="0 0 7 7" className="ds-radio__dot-icon">
                    <circle cx="3.5" cy="3.5" r="3.5" fill="currentColor" />
                  </svg>
                </span>
              </span>
            </span>
            <span className="ds-radio__content">
              <span
                className={`ds-radio__label${
                  labelVariant === "body-1" ? " ds-radio__label--body-1" : ""
                }`}
              >
                {option.label}
              </span>
              {option.description ? <span className="ds-radio__description">{option.description}</span> : null}
            </span>
          </label>
        );
      })}
    </div>
  );
}
